import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ResearchLayout from '../components/ResearchLayout';
import api from '../services/api';
import useDashboardPreferences from '../hooks/useDashboardPreferences';
import { useNavigate, Link } from 'react-router-dom';
import Preloader from '../components/Preloader';
import { getQuestions, clearQuestionsCache } from '../lib/questionsCache';

const DoctorDashboard = () => {
  const { user } = useAuth();
  const { preferences } = useDashboardPreferences();
  const navigate = useNavigate();
  const [passwordChangeHandled, setPasswordChangeHandled] = useState(false);

  const [stats, setStats] = useState({
    assignedProtocols: 0,
    totalCases: 0,
    casesAnswered: 0,
    answeredQuestions: 0,
    pendingQuestions: 0,
    pendingCases: 0,
    totalTimeRemainingMinutes: 0,
    totalTimeSpentMinutes: 0
  });
  const PER_QUESTION_MINUTES = 2; // minutes per question estimate
  const [recentAssignments, setRecentAssignments] = useState([]);
  const [continueAssignments, setContinueAssignments] = useState([]);
  const [continueAssignmentTimes, setContinueAssignmentTimes] = useState({});
  const [loading, setLoading] = useState(true);
  const [allQuestions, setAllQuestions] = useState([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [batchAssignments, setBatchAssignments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [batchWarnings, setBatchWarnings] = useState([]);
  const [dismissedWarnings, setDismissedWarnings] = useState([]);
  const [answersByCase, setAnswersByCase] = useState(new Map());
  const [continueProgress, setContinueProgress] = useState(null);  // Deferred: slow DB aggregation
  const [mainDataLoaded, setMainDataLoaded] = useState(false);
  const [batchCaseProgress, setBatchCaseProgress] = useState({});

  useEffect(() => {
    if (user) {
      // Parallel fetch all bootstrap data using Promise.all
      // Deferred: /doctors/continue-progress is slow (DB aggregation). 
      // The case list does not depend on it, so we load it after the page is interactive.
      Promise.all([
        api.get('/assignments'),
        api.get('/batches/my-batches'),
        api.get('/dashboard/dashboard-preferences')
      ]).then(([assignmentsRes, batchesRes, prefsRes]) => {
        // Process assignments
        const assignments = assignmentsRes.data?.assignments || [];
        setRecentAssignments(assignments);
        
        // BULK FETCH: Deduplicate case_ids with Set
        const caseIds = [...new Set(assignments.map(a => a.case_id).filter(Boolean))];
        
        // Calculate protocol stats
        const uniqueProtocols = [...new Set(assignments.map(a => a.protocol_id).filter(Boolean))];
        const pendingCasesCount = assignments.filter(a => a.status === 'active' || a.status === 'assigned_to_doctor').length;
        
        if (caseIds.length === 0) {
          setAnswersByCase(new Map());
          // Initialize stats with what we have
          setStats({
            assignedProtocols: uniqueProtocols.length,
            totalCases: assignments.length,
            casesAnswered: 0,
            answeredQuestions: 0,
            pendingQuestions: 0,
            pendingCases: pendingCasesCount,
            totalTimeRemainingMinutes: 0,
            totalTimeSpentMinutes: 0
          });
        } else {
          const caseIdsParam = caseIds.join(',');
          api.get(`/answers/bulk?case_ids=${caseIdsParam}&answered_by=${user?.id}`)
            .then(answersRes => {
              const allAnswers = answersRes.data?.answers || [];
              // Build Map keyed by case_id
              const answersMap = new Map();
              allAnswers.forEach(a => {
                const key = a.case_id;
                if (!answersMap.has(key)) answersMap.set(key, []);
                answersMap.get(key).push(a);
              });
              setAnswersByCase(answersMap);
              
              // Calculate stats from the loaded data
              const casesWithAnswers = answersMap.size; // distinct case_ids with answers
              const totalAnsweredQuestions = allAnswers.length;
              
              // Calculate pending questions (estimate based on protocol question counts)
              // This is a rough estimate since we don't have protocol data here
              const pendingQuestionsEstimate = Math.max(0, (assignments.length * 40) - totalAnsweredQuestions);
              
              setStats({
                assignedProtocols: uniqueProtocols.length,
                totalCases: assignments.length,
                casesAnswered: casesWithAnswers,
                answeredQuestions: totalAnsweredQuestions,
                pendingQuestions: pendingQuestionsEstimate,
                pendingCases: assignments.filter(a => a.status === 'active' || a.status === 'assigned_to_doctor').length,
                totalTimeRemainingMinutes: 0,
                totalTimeSpentMinutes: 0
              });
              
              console.log('[Dashboard] Stats calculated:', {
                assignedProtocols: uniqueProtocols.length,
                totalCases: assignments.length,
                casesWithAnswers: casesWithAnswers,
                totalAnsweredQuestions: totalAnsweredQuestions,
                pendingQuestions: pendingQuestionsEstimate
              });
            })
            .catch(err => {
              console.warn('Bulk answers fetch failed, using empty map:', err);
              setAnswersByCase(new Map());
              
              // Still calculate stats with what we have
              setStats({
                assignedProtocols: uniqueProtocols.length,
                totalCases: assignments.length,
                casesAnswered: 0,
                answeredQuestions: 0,
                pendingQuestions: 0,
                pendingCases: assignments.filter(a => a.status === 'active' || a.status === 'assigned_to_doctor').length,
                totalTimeRemainingMinutes: 0,
                totalTimeSpentMinutes: 0
              });
            });
        }
        
        // Process batches
        const myBatches = batchesRes.data?.batches || [];
        setBatches(myBatches);
        setBatchAssignments(myBatches.map(batch => ({
          batch_id: batch.id,
          status: batch.assignment_status,
          id: batch.assignment_id,
          total_cases: batch.case_count,
          can_start: batch.can_start
        })));
        
        // Process batch warnings
        const warnings = [];
        const now = new Date();
        myBatches.forEach(batch => {
          const endDate = batch.extended_end_date ? new Date(batch.extended_end_date) : new Date(batch.end_date);
          const graceDate = new Date(endDate.getTime() + (batch.grace_period_hours || 12) * 60 * 60 * 1000);
          const hoursRemaining = Math.floor((graceDate - now) / (1000 * 60 * 60));
          if (hoursRemaining < 0) {
            warnings.push({
              type: 'expired',
              batchId: batch.id,
              batchName: batch.batch_number || batch.name,
              message: `Batch "${batch.batch_number || batch.name}" has expired`,
              expiredHours: Math.abs(hoursRemaining)
            });
          } else if (hoursRemaining <= 24) {
            warnings.push({
              type: 'warning',
              batchId: batch.id,
              batchName: batch.batch_number || batch.name,
              message: `Batch "${batch.batch_number || batch.name}" expires in ${hoursRemaining} hours`,
              hoursRemaining
            });
          }
        });
        setBatchWarnings(warnings);
        
        // Fetch case progress for each batch in parallel
        const progressPromises = myBatches.map(batch =>
          api.get(`/batch-assignments/batch/${batch.id}/status`)
            .then(statusRes => {
              console.log(`[Dashboard] Progress fetch for batch ${batch.id}:`, statusRes.data.my_assignments);
              return {
                batchId: batch.id,
                progress: statusRes.data.my_assignments || { total: 0, completed: 0, remaining: 0, completion_percentage: 0 }
              };
            })
            .catch(err => {
              console.warn(`[Dashboard] Could not fetch progress for batch ${batch.id}:`, err.message);
              return {
                batchId: batch.id,
                progress: { total: 0, completed: 0, remaining: 0, completion_percentage: 0 }
              };
            })
        );
        
        Promise.all(progressPromises).then(progressResults => {
          const progressMap = {};
          progressResults.forEach(result => {
            progressMap[result.batchId] = result.progress;
          });
          setBatchCaseProgress(progressMap);
        });
        
        // Mark main data as loaded - now we can lazily load continue-progress
        setMainDataLoaded(true);
        setLoading(false);
      }).catch(err => {
        console.error('Error loading dashboard bootstrap data:', err);
        setBatches([]);
        setBatchAssignments([]);
        setBatchWarnings([]);
        setMainDataLoaded(true);  // Still allow continue-progress to load
        setLoading(false);
      });
      
      // Pre-cache questions for known protocol IDs (2 and 3)
      // This ensures child components get cached data if they call getQuestions()
      Promise.all([
        getQuestions(2),
        getQuestions(3)
      ]).catch(err => {
        console.warn('Pre-caching questions failed:', err);
      });
    }
    // eslint-disable-next-line
  }, [user]);

  // Deferred: this endpoint is slow (DB aggregation). The case list
  // does not depend on it, so we load it after the page is interactive.
  useEffect(() => {
    if (!mainDataLoaded || !user) return;
    
    api.get('/doctors/continue-progress')
      .then(res => {
        const continuePayload = res?.data || res;
        let continueAssigns = [];
        if (Array.isArray(continuePayload)) continueAssigns = continuePayload;
        else if (Array.isArray(continuePayload.assignments)) continueAssigns = continuePayload.assignments;
        else if (Array.isArray(continuePayload.data)) continueAssigns = continuePayload.data;
        setContinueAssignments(continueAssigns || []);
        setContinueProgress(continuePayload);
      })
      .catch(err => {
        console.warn('Continue-progress fetch failed:', err);
        setContinueAssignments([]);
      });
  }, [mainDataLoaded, user]);

  // Check if password change is required
  useEffect(() => {
    if (user && user.role && user.role.toLowerCase() === 'doctor' && !user.password_changed && !passwordChangeHandled) {
      setPasswordChangeHandled(true);
      navigate('/change-password');
    }
  }, [user, navigate, passwordChangeHandled]);

  // Keep pendingCases in stats synced with continueAssignments (count of cases needing continuation)
  useEffect(() => {
    // If there are no continue assignments yet, don't overwrite the server-derived value
    if (!continueAssignments || continueAssignments.length === 0) return;

    // Count unique case IDs in continueAssignments to avoid duplicates
    const uniqueCaseIds = new Set(continueAssignments.map(a => {
      // Common variants
      if (a.case_id) return String(a.case_id);
      if (a.caseId) return String(a.caseId);
      if (a.case && (a.case.id || a.case.case_id || a.case.caseId)) return String(a.case.id || a.case.case_id || a.case.caseId);
      if (a.assessment_id) return String(a.assessment_id);
      // fallback to protocol+case combo (if case missing, include protocol to avoid merging across protocols)
      return `${a.protocol_id || 'p'}_${a.case_id || a.caseId || 'unknown'}`;
    }));
    const count = uniqueCaseIds.size;
    setStats(prev => ({ ...prev, pendingCases: count }));
  }, [continueAssignments]);

  // Compute remaining questions and remaining minutes per continue-assignment (protocol+case).
  const [continueAssignmentRemaining, setContinueAssignmentRemaining] = React.useState({});
  useEffect(() => {
    let cancelled = false;
    if (!user || !continueAssignments || continueAssignments.length === 0) return;

    const fetchRemaining = async () => {
      const results = await Promise.all(continueAssignments.map(async (assignment) => {
        const key = assignment.assignment_id || `${assignment.protocol_id}_${assignment.case_id}`;

        // Try to get total questions for protocol - use cache to avoid N+1
        let totalQuestions = 0;
        try {
          const cachedQuestionsResponse = await getQuestions(assignment.protocol_id);
          const cachedQuestions = cachedQuestionsResponse.questions || [];
          totalQuestions = cachedQuestions.length;
        } catch (err) {
          // fallback: if assignment carries a field, try to use it
          totalQuestions = assignment.assigned_question_count || assignment.total_questions || 0;
        }

        // Count answers for this protocol+case by this doctor - use pre-fetched Map
        const caseId = assignment.case_id;
        const answersForCase = answersByCase.get(caseId) || [];
        const answeredCount = answersForCase.length;

        const remainingQuestions = Math.max(0, (totalQuestions || 0) - (answeredCount || 0));
        const remainingMinutes = remainingQuestions * PER_QUESTION_MINUTES;
        return [key, { remainingQuestions, remainingMinutes }];
      }));

      if (!cancelled) {
        const map = {};
        results.forEach(([k, v]) => { map[k] = v; });
        setContinueAssignmentRemaining(map);
      }
    };

    fetchRemaining();
    return () => { cancelled = true; };
  }, [continueAssignments, user, answersByCase]);

  const loadBatchData = async () => {
    try {
      // Use the correct endpoint for doctors to get their assigned batches
      const myBatchesRes = await api.get('/batches/my-batches');
      const myBatches = myBatchesRes.data.batches || [];
      
      // Update state with processed batch data
      setBatchAssignments(myBatches.map(batch => ({
        batch_id: batch.id,
        status: batch.assignment_status,
        id: batch.assignment_id,
        total_cases: batch.case_count,
        can_start: batch.can_start
      })));
      setBatches(myBatches);

      // Check for batch warnings
      const warnings = [];
      const now = new Date();
      
      myBatches.forEach(batch => {
        // Use extended_end_date if available, otherwise use end_date
        const endDate = batch.extended_end_date ? new Date(batch.extended_end_date) : new Date(batch.end_date);
        const graceDate = new Date(endDate.getTime() + (batch.grace_period_hours || 12) * 60 * 60 * 1000);
        const hoursRemaining = Math.floor((graceDate - now) / (1000 * 60 * 60));
        
        if (hoursRemaining < 0) {
          warnings.push({
            type: 'expired',
            batchId: batch.id,
            batchName: batch.batch_number || batch.name,
            message: `Batch "${batch.batch_number || batch.name}" has expired`,
            expiredHours: Math.abs(hoursRemaining)
          });
        } else if (hoursRemaining <= 24) {
          warnings.push({
            type: 'warning',
            batchId: batch.id,
            batchName: batch.batch_number || batch.name,
            message: `Batch "${batch.batch_number || batch.name}" expires in ${hoursRemaining} hours`,
            hoursRemaining
          });
        }
      });
      
      setBatchWarnings(warnings);
    } catch (error) {
      console.error('Error loading batch data:', error);
      setBatchAssignments([]);
      setBatches([]);
      setBatchWarnings([]);
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      let assignmentsData = { assignments: [] };
      let answersData = { answers: [] };
      let protocolsData = { protocols: [] };
      let casesData = { cases: [] };
      let progressReportData = null;

      // Fetch progress report data to get accurate cases answered count
      try {
        const progressRes = await api.get('/doctors/progress-report');
        progressReportData = progressRes.data;
      } catch (error) {
        console.error('Failed to load progress report:', error);
      }

      // Fetch assignments
      try {
        const assignmentsRes = await api.get(`/assignments?doctor_id=${user.id}&status=active,assigned_to_doctor`);
        assignmentsData = assignmentsRes.data;
      } catch (error) {
        console.error('Failed to load assignments:', error);
      }

      // Fetch answers
      try {
        const answersRes = await api.get(`/answers?answered_by=${user.id}`);
        answersData = answersRes.data;
      } catch (error) {
        console.error('Failed to load answers:', error);
      }

      // Fetch protocols
      try {
        const protocolsRes = await api.get('/protocols');
        protocolsData = protocolsRes.data;
      } catch (error) {
        console.error('Failed to load protocols:', error);
      }

      // Fetch cases
      try {
        const casesRes = await api.get('/cases/view');
        casesData = casesRes.data;
      } catch (error) {
        console.error('Failed to load cases:', error);
      }

      // Defensive: Ensure arrays
      const assignments = Array.isArray(assignmentsData.assignments) ? assignmentsData.assignments : [];
      const protocols = Array.isArray(protocolsData.protocols) ? protocolsData.protocols : [];
      const cases = Array.isArray(casesData.cases) ? casesData.cases : [];
      const answers = Array.isArray(answersData.answers) ? answersData.answers : [];

      // Fetch questions here to ensure we have them for pending calculations
      let questions = Array.isArray(allQuestions) ? allQuestions : [];
      try {
        const questionsRes = await api.get('/questions');
        questions = Array.isArray(questionsRes.data.questions) ? questionsRes.data.questions : (questionsRes.data?.questions || []);
      } catch (err) {
        // keep local allQuestions fallback
      }

      // Filter assignments for valid protocol and case IDs
      const doctorAssignments = assignments.filter(
        a => (a.status === 'active' || a.status === 'assigned_to_doctor') && a.protocol_id && a.case_id
      );

      // Group assignments by protocol_id
      const groupedAssignments = doctorAssignments.reduce((acc, assignment) => {
        const key = assignment.protocol_id;
        if (!acc[key]) {
          acc[key] = {
            id: assignment.protocol_id,
            protocolId: assignment.protocol_id,
            protocolName: assignment.protocol_name || 'Unknown Protocol',
            assignedAt: assignment.assigned_at,
            status: assignment.status,
            case_ids: []
          };
        }
        if (!acc[key].case_ids.includes(assignment.case_id)) {
          acc[key].case_ids.push(assignment.case_id);
        }
        return acc;
      }, {});

      const uniqueAssignments = Object.values(groupedAssignments);

      // Stats - Count unique cases across all protocols to avoid double counting
      const allAssignedCaseIds = new Set();
      uniqueAssignments.forEach(assignment => {
        (assignment.case_ids || []).forEach(caseId => {
          allAssignedCaseIds.add(caseId);
        });
      });
      const totalCases = allAssignedCaseIds.size;

      // --- Calculate Pending Questions & Pending Cases Functionally ---
      // Use allQuestions to get questions for each protocol and answers to detect progress
      let totalQuestions = 0;
      const answeredQuestionIds = new Set(answers.map(a => `${a.protocol_id}_${a.case_id}_${a.question_id}`));

      // Build a map of answered counts per protocol_case
      const answeredCountByCase = {};
      answers.forEach(a => {
        const key = `${a.protocol_id}_${a.case_id}`;
        answeredCountByCase[key] = (answeredCountByCase[key] || 0) + 1;
      });

      let pendingCasesCount = 0;
      let pendingQuestionsLocal = 0;

      uniqueAssignments.forEach(assignment => {
        // Get ONLY ACTIVE questions for this protocol
        const protocolQuestions = questions.filter(q => q.protocol_id === assignment.protocolId && q.is_active !== false);
        const totalQsForProtocol = protocolQuestions.length;
        assignment.case_ids.forEach(caseId => {
          totalQuestions += totalQsForProtocol;
          const key = `${assignment.protocolId}_${caseId}`;
          const answeredForCase = answeredCountByCase[key] || 0;
          const remaining = Math.max(0, totalQsForProtocol - answeredForCase);
          if (remaining > 0) pendingCasesCount += 1;
          pendingQuestionsLocal += remaining;
        });
      });

      // Fetch pending questions count from backend (fallback / authoritative)
      let pendingQuestions = 0;
      try {
        const pendingRes = await api.get(`/questions/pending-count/${user.id}`);
        pendingQuestions = pendingRes.data.pending_questions || 0;
      } catch (error) {
        console.error('Failed to fetch pending questions count:', error);
        // Fallback to local calculation if API fails
        pendingQuestions = pendingQuestionsLocal;
      }

      // Prefer server pendingQuestions when present, otherwise fall back to locally computed count
      const pendingQuestionsFinal = pendingQuestions || pendingQuestionsLocal;
      const totalTimeRemainingMinutes = Math.ceil(pendingQuestionsFinal * PER_QUESTION_MINUTES);

      // If continueAssignments provides a case count, prefer it when it's larger
      const continueCaseCount = Array.isArray(continueAssignments) && continueAssignments.length > 0
        ? new Set(continueAssignments.map(a => {
            if (a.case_id) return String(a.case_id);
            if (a.caseId) return String(a.caseId);
            if (a.case && (a.case.id || a.case.case_id || a.case.caseId)) return String(a.case.id || a.case.case_id || a.case.caseId);
            if (a.assessment_id) return String(a.assessment_id);
            return `${a.protocol_id || 'p'}_${a.case_id || a.caseId || 'unknown'}`;
          })).size
        : 0;

      const pendingCasesToSet = Math.max(pendingCasesCount, continueCaseCount || 0);

      // Use cases answered from progress report for consistency, with improved fallback
      let casesAnswered = 0;
      if (progressReportData && progressReportData.overall_stats) {
        casesAnswered = progressReportData.overall_stats.total_cases_answered || 0;
      } else {
        // Improved fallback calculation if progress report fails
        const completedCases = new Set();
        uniqueAssignments.forEach(assignment => {
          // Only count ACTIVE questions for completion calculation
          const protocolQuestions = questions.filter(q => q.protocol_id === assignment.protocolId && q.is_active !== false);
          const totalQsForProtocol = protocolQuestions.length;
          
          assignment.case_ids.forEach(caseId => {
            const key = `${assignment.protocolId}_${caseId}`;
            const answeredForCase = answeredCountByCase[key] || 0;
            // Only count as completed if all ACTIVE questions are answered and there are questions to answer
            if (answeredForCase >= totalQsForProtocol && totalQsForProtocol > 0) {
              // Add the case ID itself, not the protocol+case combination
              // This ensures we count unique cases, not protocol-case combinations
              completedCases.add(caseId);
            }
          });
        });
        casesAnswered = completedCases.size;
      }

      // Calculate time spent (2 minutes per question answered)
      const totalTimeSpentMinutes = answers.length * 2;

      setStats({
        assignedProtocols: uniqueAssignments.length,
        totalCases,
        casesAnswered,
        answeredQuestions: answers.length,
        pendingQuestions: pendingQuestionsFinal,
        pendingCases: pendingCasesToSet,
        totalTimeRemainingMinutes,
        totalTimeSpentMinutes
      });

      // Prepare recent assignments with protocol details and case data
      const recentWithDetails = uniqueAssignments.map(assignment => {
        const protocolDetails = protocols.find(p => p.id === assignment.protocolId);
        const assignmentCases = (assignment.case_ids || []).map(caseId => {
          const caseDetails = cases.find(c => c.id === caseId);
          return caseDetails ? {
            ...caseDetails,
            id: caseId,
            customerId: caseDetails.customerId || caseDetails.customer_id || 'Unknown'
          } : null;
        }).filter(Boolean);

        return {
          id: assignment.id || `protocol-${assignment.protocolId}`,
          protocolId: assignment.protocolId,
          protocolName: protocolDetails?.name || assignment.protocolName || 'Unknown Protocol',
          protocolDescription: protocolDetails?.description || '',
          assignedAt: assignment.assignedAt,
          status: assignment.status,
          case_ids: assignment.case_ids,
          cases: assignmentCases
        };
      });

      setRecentAssignments(recentWithDetails);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setStats({
        assignedProtocols: 0,
        totalCases: 0,
        answeredQuestions: 0,
        pendingQuestions: 0
      });
      setRecentAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  const getHealthColor = (prediction) => {
    switch (prediction?.toLowerCase()) {
      case 'excellent': return 'text-success';
      case 'good': return 'text-info';
      case 'fair': return 'text-warning';
      case 'at risk':
      case 'needs attention':
      case 'high risk':
      case 'critical': return 'text-danger';
      default: return 'text-muted';
    }
  };

  // Check if role-specific sections should be shown
  const shouldShowSection = (sectionKey) => {
    // Prefer in-memory preferences from hook
    const roleSpecificSections = (preferences && preferences.role_specific_sections) || {};
    // Support synonyms: some saved prefs use 'recent_protocols' while UI checks 'protocol_assignments'
    const synonyms = {
      protocol_assignments: ['recent_protocols'],
      recent_protocols: ['protocol_assignments']
    };

    const checkFalse = (obj, key) => {
      if (!obj) return false;
      if (obj[key] === false) return true;
      const alts = synonyms[key] || [];
      return alts.some((alt) => obj[alt] === false);
    };

    if (checkFalse(roleSpecificSections, sectionKey)) return false;

    // Fallback to localStorage (saved by SystemSettingsModal) to cover race conditions
    try {
      const stored = JSON.parse(localStorage.getItem('dashboard_preferences') || '{}');
      const storedRole = stored.role_specific_sections || {};
      if (checkFalse(storedRole, sectionKey)) return false;
    } catch (e) {
      // ignore parse errors and assume visible
    }

    // Default to visible
    return true;
  };

  const handleContinue = (assignment) => {
    // Build query parameters - include batch if available for batch-specific workflows
    const params = new URLSearchParams({
      protocol: assignment.protocol_id,
      case: assignment.case_id
    });
    
    // Add batch parameter if assignment has batch information
    if (assignment.batch_id) {
      params.append('batch', assignment.batch_id);
    }
    
    navigate(`/doctor/answer-questions/start?${params.toString()}`);
  };

  const getBatchStatus = (batch) => {
    const now = new Date();
    const startDate = new Date(batch.start_date);
    const endDate = batch.extended_end_date ? new Date(batch.extended_end_date) : new Date(batch.end_date);
    const graceDate = new Date(endDate.getTime() + (batch.grace_period_hours || 12) * 60 * 60 * 1000);
    
    if (now < startDate) {
      return 'future';
    } else if (now > graceDate) {
      return 'expired';
    } else if (now > endDate) {
      return 'grace_period';
    } else {
      return 'active';
    }
  };

  const getBatchStatusInfo = (status) => {
    switch (status) {
      case 'future':
        return { text: 'Future', class: 'badge bg-secondary' };
      case 'expired':
        return { text: 'Expired', class: 'badge bg-danger' };
      case 'grace_period':
        return { text: 'Grace Period', class: 'badge bg-warning' };
      case 'active':
        return { text: 'Active', class: 'badge bg-success' };
      default:
        return { text: 'Unknown', class: 'badge bg-secondary' };
    }
  };

  const dismissWarning = (batchId) => {
    setDismissedWarnings(prev => [...prev, batchId]);
  };

  if (loading) {
    return (
      <ResearchLayout pageTitle="Doctor Dashboard">
        <Preloader message="Loading your dashboard..." />
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout pageTitle="Doctor Dashboard">
      <div className="container-fluid">
        {/* Welcome Section - conditionally rendered based on preferences */}
        {preferences.welcome_section_visible && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm bg-gradient-primary text-white">
                <div className="card-body" style={{backgroundColor: '#7B68EE'}}>
                  <h3 className="mb-2">Welcome back, {user?.name}!</h3>
                  <p className="mb-0 opacity-75">
                    Here's an overview of your research assignments and batch deadlines.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Batch Warnings */}
        {batchWarnings.filter(warning => !dismissedWarnings.includes(warning.batchId)).length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              {batchWarnings
                .filter(warning => !dismissedWarnings.includes(warning.batchId))
                .map((warning, index) => {
                  // Get the actual batch data to use the backend's time_remaining calculation
                  const batch = batches.find(b => b.id === warning.batchId);
                  const timeRemaining = batch?.time_remaining;
                  
                  return (
                    <div key={`${warning.batchId}-${index}`} className={`alert ${warning.type === 'expired' ? 'alert-danger' : 'alert-warning'} border-0 shadow-sm mb-3`} role="alert">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <i className={`fas fa-${warning.type === 'expired' ? 'exclamation-triangle' : 'clock'} me-3`}></i>
                          <div>
                            <strong>{warning.message}</strong>
                            {timeRemaining && warning.type === 'warning' && (
                              <p className="mb-0 mt-1">Time remaining: {timeRemaining}</p>
                            )}
                            {warning.type === 'warning' && (
                              <p className="mb-0 mt-1">Complete your remaining cases before the deadline.</p>
                            )}
                            {warning.type === 'expired' && (
                              <p className="mb-0 mt-1">Your completed work in this batch has been saved.</p>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => dismissWarning(warning.batchId)}
                          aria-label="Dismiss warning"
                        ></button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Statistics Cards - conditionally rendered based on preferences */}
        {preferences.stats_cards_visible && (
          <div className="row mb-4">
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-primary mb-2">
                    <i className="fas fa-clipboard-list fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-primary">{stats.assignedProtocols}</h4>
                  <p className="text-muted mb-0">Assigned Protocols</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-success mb-2">
                    <i className="fas fa-users fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-success">{stats.totalCases}</h4>
                  <p className="text-muted mb-0">Total Cases</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-warning mb-2">
                    <i className="fas fa-tasks fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-warning">{stats.casesAnswered}/{stats.totalCases}</h4>
                  <p className="text-muted mb-0">Cases Answered</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-info mb-2">
                    <i className="fas fa-check-circle fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-info">{stats.answeredQuestions}</h4>
                  <p className="text-muted mb-0">Answered Questions</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-danger mb-2">
                    <i className="fas fa-clock fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-danger">{stats.pendingQuestions}</h4>
                  <p className="text-muted mb-0">Pending Questions</p>
                </div>
              </div>
            </div>
            
            {/* <div className="col-lg-3 col-md-6 mb-3">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body text-center">
                  <div className="text-danger mb-2">
                    <i className="fas fa-exclamation-circle fa-2x"></i>
                  </div>
                  <h4 className="fw-bold text-danger">{stats.pendingCases}</h4>
                  <p className="text-muted mb-0">Pending Cases</p>
                </div>
              </div>
            </div> */}

          </div>
        )}

        {/* Batch Assignments - role-specific section */}
        {shouldShowSection('protocol_assignments') && (
          <div className="row">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0">
                    <i className="fas fa-folder-open text-primary me-2"></i>
                    My Batch Assignments ({batchAssignments.length})
                  </h5>
                </div>
                <div className="card-body">
                  {batchAssignments.length > 0 ? (
                    <div className="row">
                      {batchAssignments.map((batchAssignment) => {
                        const batch = batches.find(b => b.id === batchAssignment.batch_id);
                        if (!batch) return null;
                        
                        const status = getBatchStatus(batch);
                        const statusInfo = getBatchStatusInfo(status);
                        const endDate = batch.extended_end_date ? new Date(batch.extended_end_date) : new Date(batch.end_date);
                        const graceDate = new Date(endDate.getTime() + (batch.grace_period_hours || 12) * 60 * 60 * 1000);
                        const now = new Date();
                        const hoursRemaining = Math.floor((graceDate - now) / (1000 * 60 * 60));
                        
                        return (
                          <div key={batchAssignment.id} className="col-lg-4 col-md-6 mb-3">
                            <div className="card border border-secondary h-100">
                              <div className="card-header bg-light">
                                <h6 className="mb-0">
                                  <i className="fas fa-folder me-2"></i>
                                  {batch.batch_number || batch.name}
                                </h6>
                              </div>
                              <div className="card-body">
                                <p className="text-muted small mb-3">
                                  {batch.description || 'No description available'}
                                </p>

                                {/* Case Progress Section */}
                                {batchCaseProgress[batchAssignment.batch_id] ? (
                                  <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <small className="text-muted fw-bold">
                                        <i className="fas fa-tasks me-1"></i>
                                        Progress
                                      </small>
                                      <small className="badge bg-info">
                                        {batchCaseProgress[batchAssignment.batch_id].completed || 0}/{batchCaseProgress[batchAssignment.batch_id].total || 0}
                                      </small>
                                    </div>
                                    
                                    {/* Progress Bar */}
                                    <div className="progress" style={{ height: '18px' }}>
                                      <div
                                        className="progress-bar bg-success"
                                        role="progressbar"
                                        style={{
                                          width: `${Math.max(batchCaseProgress[batchAssignment.batch_id].completion_percentage || 0, batchCaseProgress[batchAssignment.batch_id].total === 0 ? 0 : 0)}%`
                                        }}
                                        aria-valuenow={batchCaseProgress[batchAssignment.batch_id].completed || 0}
                                        aria-valuemin="0"
                                        aria-valuemax={batchCaseProgress[batchAssignment.batch_id].total || 0}
                                      >
                                        {batchCaseProgress[batchAssignment.batch_id].total > 0 && (
                                          <small className="text-white fw-bold">
                                            {Math.round(batchCaseProgress[batchAssignment.batch_id].completion_percentage || 0)}%
                                          </small>
                                        )}
                                      </div>
                                    </div>

                                    {/* Status Message */}
                                    {batchCaseProgress[batchAssignment.batch_id].total === 0 && (
                                      <small className="text-muted d-block mt-1">
                                        <i className="fas fa-info-circle me-1"></i>
                                        No cases assigned yet
                                      </small>
                                    )}
                                  </div>
                                ) : null}
                                
                                <div className="mb-3">
                                  <div className="row mb-2">
                                    <div className="col-6">
                                      <small className="text-muted">Cases</small>
                                      <div className="fw-bold">
                                        {batchAssignment.total_cases || 'N/A'}
                                      </div>
                                    </div>
                                    <div className="col-6">
                                      <small className="text-muted">Status</small>
                                      <div>
                                        <span className={statusInfo.class}>
                                          {statusInfo.text}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="mb-3">
                                  <small className="text-muted">Timeline</small>
                                  <div className="small">
                                    <div>
                                      <i className="fas fa-play me-1"></i>
                                      {new Date(batch.start_date).toLocaleDateString()}
                                    </div>
                                    <div>
                                      <i className="fas fa-stop me-1"></i>
                                      {endDate.toLocaleDateString()}
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="d-flex justify-content-between align-items-center">
                                  {status === 'future' && (
                                    <small className="text-muted">
                                      <i className="fas fa-clock me-1"></i>
                                      Starts: {new Date(batch.start_date).toLocaleDateString()}
                                    </small>
                                  )}
                                  {status === 'active' && (
                                    <small className="text-warning">
                                      <i className="fas fa-hourglass-half me-1"></i>
                                      {batch.time_remaining || `${hoursRemaining}h remaining`}
                                    </small>
                                  )}
                                  {status === 'grace_period' && (
                                    <small className="text-warning">
                                      <i className="fas fa-exclamation-triangle me-1"></i>
                                      {batch.time_remaining || `${hoursRemaining}h grace period`}
                                    </small>
                                  )}
                                  {status === 'expired' && (
                                    <small className="text-danger">
                                      <i className="fas fa-times-circle me-1"></i>
                                      {batch.time_remaining || 'Expired'}
                                    </small>
                                  )}
                                </div>
                                
                                {status === 'active' || status === 'grace_period' ? (
                                  <div className="mt-3">
                                    <Link
                                      to="/doctor/answer-questions"
                                      className="btn btn-primary btn-sm w-100"
                                    >
                                      <i className="fas fa-play me-1"></i>
                                      Continue Work
                                    </Link>
                                  </div>
                                ) : status === 'future' ? (
                                  <div className="mt-3">
                                    <button
                                      className="btn btn-secondary btn-sm w-100"
                                      disabled
                                    >
                                      <i className="fas fa-clock me-1"></i>
                                      Not Started
                                    </button>
                                  </div>
                                ) : (
                                  <div className="mt-3">
                                    <Link
                                      to="/doctor/reports"
                                      className="btn btn-outline-info btn-sm w-100"
                                    >
                                      <i className="fas fa-chart-bar me-1"></i>
                                      View Report
                                    </Link>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="fas fa-folder-open fa-3x text-muted mb-3"></i>
                      <h5 className="text-muted">No Batch Assignments</h5>
                      <p className="text-muted">
                        You haven't been assigned any batches yet. Check back later or contact your Medical Admin.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upcoming Appointments - role-specific section */}
        {/* {shouldShowSection('upcoming_appointments') && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0">
                    <i className="fas fa-calendar-alt text-info me-2"></i>
                    Upcoming Appointments
                  </h5>
                </div>
                <div className="card-body">
                  <div className="text-center py-4">
                    <i className="fas fa-calendar-alt fa-3x text-muted mb-3"></i>
                    <h5 className="text-muted">No Upcoming Appointments</h5>
                    <p className="text-muted">Your upcoming appointments will appear here.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )} */}

        {/* Quick Actions - conditionally rendered based on preferences */}
        {preferences.quick_actions_visible && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <h5 className="mb-0">
                    <i className="fas fa-bolt text-warning me-2"></i>
                    Quick Actions
                  </h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-3 mb-3">
                      <Link to="/doctor/answer-questions" className="btn btn-outline-primary w-100 p-3">
                        <i className="fas fa-question-circle fa-2x mb-2"></i>
                        <br />
                        Review Cases
                      </Link>
                    </div>
                    {/* <div className="col-md-3 mb-3">
                      <Link to="/doctor/cases" className="btn btn-outline-success w-100 p-3">
                        <i className="fas fa-users fa-2x mb-2"></i>
                        <br />
                        View My Cases
                      </Link>
                    </div> */}
                    {/* <div className="col-md-3 mb-3">
                      <Link to="/doctor/protocols" className="btn btn-outline-info w-100 p-3">
                        <i className="fas fa-clipboard-list fa-2x mb-2"></i>
                        <br />
                        My Protocols
                      </Link>
                    </div> */}
                    <div className="col-md-3 mb-3">
                      <Link to="/doctor/reports" className="btn btn-outline-secondary w-100 p-3">
                        <i className="fas fa-chart-bar fa-2x mb-2"></i>
                        <br />
                        Progress Report
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Continue Button - shown if there's incomplete progress */}
        {continueAssignments.length > 0 && (
          <div className="row mt-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center">
                  <h5>Continue Unanswered Assignments</h5>
                  <p className="text-muted mb-3">
                    You have {continueAssignments.length} assignment{continueAssignments.length > 1 ? 's' : ''} waiting to be completed.
                  </p>
                  <button
                    className="btn btn-warning btn-lg"
                    onClick={() => setShowAssignmentModal(true)}
                  >
                    <i className="fas fa-play-circle me-2"></i>
                    Continue Assignments ({continueAssignments.length})
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assignment Selection Modal */}
        {showAssignmentModal && (
          <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header bg-warning text-dark">
                  <h5 className="modal-title">
                    <i className="fas fa-clipboard-list me-2"></i>
                    Select Assignment to Continue
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowAssignmentModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    {continueAssignments.map((assignment, idx) => {
                      const key = assignment.assignment_id || `${assignment.protocol_id}_${assignment.case_id}`;
                      const remaining = continueAssignmentRemaining[key];
                      const remainingQs = remaining ? remaining.remainingQuestions : null;
                      const remainingMins = remaining ? remaining.remainingMinutes : null;

                      return (
                        <div key={assignment.assignment_id || `${assignment.protocol_id}-${assignment.case_id}-${idx}`} className="col-12 mb-3">
                          <div className="card border border-warning">
                            <div className="card-body">
                              <div className="row align-items-center">
                                <div className="col-md-8">
                                  <h6 className="card-title mb-1">
                                    <i className="fas fa-file-medical me-2 text-primary"></i>
                                    {assignment.protocol_name}
                                  </h6>
                                  <p className="text-muted mb-2">
                                    <i className="fas fa-user me-2"></i>
                                    Case ID: <strong>{assignment.case_id}</strong>
                                  </p>
                                  <div className="d-flex gap-3">
                                    <span className="badge bg-info">
                                      <i className="fas fa-question-circle me-1"></i>
                                      {remainingQs !== null ? `${remainingQs} questions` : 'Loading...'}
                                    </span>
                                    <span className="badge bg-secondary">
                                      <i className="fas fa-clock me-1"></i>
                                      {remainingMins !== null ? `${remainingMins} min` : 'Loading...'}
                                    </span>
                                  </div>
                                </div>
                                <div className="col-md-4 text-end">
                                  <button
                                    className="btn btn-warning"
                                    onClick={() => {
                                      setShowAssignmentModal(false);
                                      handleContinue(assignment);
                                    }}
                                  >
                                    <i className="fas fa-play me-2"></i>
                                    Start
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowAssignmentModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ResearchLayout>
  );
};

export default DoctorDashboard;